/**
 * PlanLimitOverlay — "you can't do this on your plan this month", as a
 * modal over the CURRENT screen (nothing navigates; closing it puts the
 * merchant back exactly where they were, inputs intact).
 *
 * Opened by the PLAN_LIMIT_EVENT (lib/quotaEvents) from two places:
 *   - lib/quota.js   ensureQuota() — the pre-check before any
 *                    quota-consuming action (needed / remaining known)
 *   - lib/api.js     the central fetch wrapper, on ANY 403 + upgrade +
 *                    quota cap code from ANY endpoint (the server is
 *                    the authority)
 *
 * Above the cards, one clear line:
 *   reached     "You've used all {N} {feature} on {Plan} this month.
 *                Upgrade to keep creating — your limit resets on {date}."
 *   not enough  "You have {X} {feature} left this month. This needs {Y}.
 *                Upgrade your plan to do this now."
 * Cards: the ChoosePlanScreen cards (current plan marked, each card
 * showing that feature's monthly allowance); choosing one runs the
 * existing Shopify billing approval flow and remembers where to come
 * back to (localStorage, best effort) so the post-approval reload lands
 * on the same screen with a "Plan upgraded — you can generate now." toast.
 * On the top tier there is no upgrade card — "Contact us for a Custom
 * plan" with the support link instead.
 *
 * Hosts (mounted once in AppLayout): <PlanLimitOverlayHost plan=…/> and
 * <QuotaToastHost /> (the small non-blocking near-limit / upgraded toast).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiGet } from '../lib/api';
import useBilling from '../hooks/useBilling';
import { useTranslation } from '../i18n';
import { HUB_ROUTE } from '../lib/affiliate';
import { PLAN_LIMIT_EVENT, QUOTA_TOAST_EVENT } from '../lib/quotaEvents';
import {
  fetchUsage, featureRow, remainingUnits, limitUnits, featureLabel, unitsLabel, formatUnits, lastNeededFor, notifyUsageChanged, showQuotaToast,
} from '../lib/quota';
import { PlanCard, SUPPORT_HREF, formatQuota } from './ChoosePlanScreen';
import './PlanLimitOverlay.css';

const RETURN_KEY = 'autonoma_plan_return';
const RETURN_TTL_MS = 30 * 60 * 1000;

/** Which display key of plans[tier].features carries a quota feature. */
const DISPLAY_KEY = {
  productBuilds: 'productsPerMonth',
  imageAds: 'imageAdsPerMonth',
  videoAds: 'videosPerMonth',
  advertorials: 'advertorialsPerMonth',
  findProductsSearches: 'findProductsPerMonth',
  adCopy: 'adCopyRunsPerMonth',
  upsells: 'upsellSearchesPerMonth',
  splitTests: 'splitTestsPerMonth',
  changesBudget: 'changesBudget',
};

export function saveReturnIntent(intent) {
  try { window.localStorage.setItem(RETURN_KEY, JSON.stringify({ ...intent, ts: Date.now() })); } catch { /* best effort */ }
}
export function takeReturnIntent() {
  try {
    const raw = window.localStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    window.localStorage.removeItem(RETURN_KEY);
    const intent = JSON.parse(raw);
    if (!intent || !intent.ts || Date.now() - intent.ts > RETURN_TTL_MS) return null;
    return intent;
  } catch { return null; }
}

/** "40 product builds / month" · "$12.00 changes allowance / month" · "Unlimited …" · "Not on this plan". */
export function allowanceLine(plan, feature, row, t, language) {
  const key = DISPLAY_KEY[feature];
  const value = key ? plan?.features?.[key] : undefined;
  const label = featureLabel(row, feature);
  if (value === 'unlimited' || value === Infinity) return t('planLimit.allowanceUnlimited', { feature: label });
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return t('planLimit.allowanceNone');
  if (feature === 'changesBudget') return t('planLimit.allowanceBudget', { n: `$${n.toFixed(2)}`, feature: label });
  return t('planLimit.allowance', { n: formatQuota(n, language), feature: label });
}

/**
 * The headline + message for a request, from the live usage row.
 * `request`: { feature, code, needed?, remaining?, message?, source }
 */
export function composeMessage(request, usage, t, language) {
  const feature = request.feature || 'changesBudget';
  const row = featureRow(usage, feature);
  const label = featureLabel(row, feature);
  const budget = feature === 'changesBudget';
  const remaining = request.remaining != null ? request.remaining : remainingUnits(row);
  const needed = request.needed || lastNeededFor(feature);
  const limit = limitUnits(row);
  const plan = usage?.planName || '';
  const date = usage?.resetDate || nextResetLabel(language);
  const fmt = (units) => formatUnits(row || { unit: budget ? 'dollars' : 'count' }, units);

  if (request.source === 'toast' && remaining != null && remaining > 0 && (!needed || remaining >= needed)) {
    return { kind: 'near', title: t('planLimit.titleNear'), text: t('planLimit.left', { x: fmt(remaining), feature: unitsLabel(row, feature, remaining) }) };
  }
  if (remaining != null && remaining > 0 && remaining !== Infinity) {
    const leftLabel = unitsLabel(row, feature, remaining);
    if (needed && needed > remaining) {
      return { kind: 'notEnough', title: t('planLimit.titleNotEnough'), text: t(budget ? 'planLimit.notEnoughBudget' : 'planLimit.notEnough', { x: fmt(remaining), feature: leftLabel, y: fmt(needed) }) };
    }
    return { kind: 'notEnough', title: t('planLimit.titleNotEnough'), text: request.message || t('planLimit.notEnoughUnknown', { x: fmt(remaining), feature: leftLabel }) };
  }
  if (Number.isFinite(limit)) {
    return { kind: 'reached', title: t('planLimit.titleReached'), text: t(budget ? 'planLimit.reachedBudget' : 'planLimit.reached', { n: fmt(limit), feature: unitsLabel(row, feature, limit), plan, date }) };
  }
  // Usage unreadable (or an unlimited plan the server still refused):
  // fall back to the server's own sentence.
  return { kind: 'reached', title: t('planLimit.titleReached'), text: request.message || t('planLimit.reached', { n: '', feature: label, plan, date }).replace(/\s+/g, ' ') };
}

function nextResetLabel(language) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  try { return d.toLocaleDateString(language || undefined, { month: 'long', day: 'numeric' }); } catch { return d.toDateString(); }
}

export function PlanLimitOverlayHost({ plan: currentPlanFromShell = null }) {
  const [request, setRequest] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handledReturn = useRef(false);

  useEffect(() => {
    const onOpen = (e) => setRequest((prev) => prev || { ...(e.detail || {}) });
    window.addEventListener(PLAN_LIMIT_EVENT, onOpen);
    return () => window.removeEventListener(PLAN_LIMIT_EVENT, onOpen);
  }, []);

  // Back from Shopify's approval screen: the app reloads at its root —
  // go back to where the upgrade was started and say it worked (only
  // when the plan actually changed; a declined charge just returns).
  useEffect(() => {
    if (handledReturn.current || currentPlanFromShell == null) return;
    const intent = takeReturnIntent();
    handledReturn.current = true;
    if (!intent) return;
    if (intent.path && intent.path !== `${window.location.pathname}${window.location.search}`) navigate(intent.path, { replace: true });
    if (intent.fromPlan && intent.fromPlan !== currentPlanFromShell) {
      notifyUsageChanged();
      // Next tick: the toast host may mount right after this host (same
      // commit) — its listener must exist before the event fires. (No
      // cleanup on purpose: the navigate() above re-runs this effect.)
      const message = t('planLimit.upgraded');
      setTimeout(() => showQuotaToast(message, { tone: 'success' }), 0);
    }
  }, [currentPlanFromShell, navigate, t]);

  if (!request) return null;
  return <PlanLimitOverlay request={request} onClose={() => setRequest(null)} />;
}

export default function PlanLimitOverlay({ request, onClose }) {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { subscribe } = useBilling();
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const titleId = 'autonoma-plan-limit-title';

  useEffect(() => {
    let alive = true;
    fetchUsage({ force: true }).then((u) => { if (alive) setUsage(u || {}); });
    apiGet(`/api/billing/plans${language ? `?locale=${encodeURIComponent(language)}` : ''}`)
      .then((r) => r.json())
      .then((d) => { if (alive) setPlans({ plans: d.plans || {}, trialUsed: d.trialUsed === true }); })
      .catch(() => { if (alive) setError(t('planLimit.loadError')); });
    return () => { alive = false; };
  }, [language, t]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey, true); document.body.style.overflow = prev; };
  }, [onClose]);

  const feature = request.feature || 'changesBudget';
  const row = featureRow(usage, feature);
  const message = useMemo(() => (usage ? composeMessage(request, usage, t, language) : null), [request, usage, t, language]);
  const orderedKeys = useMemo(() => Object.keys(plans?.plans || {}), [plans]);
  const currentPlan = usage?.plan || null;
  const currentIdx = orderedKeys.indexOf(currentPlan);
  const topTier = orderedKeys.length > 0 && currentIdx === orderedKeys.length - 1;
  const cardKeys = currentIdx >= 0 ? orderedKeys.slice(currentIdx) : orderedKeys;

  const choose = useCallback(async (planKey) => {
    if (busy) return;
    setBusy(planKey);
    setError(null);
    try {
      saveReturnIntent({ path: `${location.pathname}${location.search}`, fromPlan: currentPlan, feature });
      const d = await subscribe(planKey);
      if (!d?.confirmationUrl) throw new Error('no confirmation url');
      // Shopify's consent screen cannot render inside the embedded
      // iframe — break out at the top window (same as ChoosePlanScreen).
      (window.top || window).location.href = d.confirmationUrl;
    } catch {
      setError(t('planLimit.subscribeError'));
      setBusy(null);
    }
  }, [busy, location.pathname, location.search, currentPlan, feature, subscribe, t]);

  const openReferrals = () => { onClose(); navigate(HUB_ROUTE); };

  return createPortal(
    <div className="au-plans-overlay au-pl-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="plan-limit-overlay" data-kind={message?.kind || 'loading'}>
      <div className="au-pl" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="au-pl__head">
          <div className="au-pl__eyebrow">{featureLabel(row, feature)}</div>
          <h2 id={titleId} className="au-pl__title">{message ? message.title : '\u00a0'}</h2>
          <p className="au-pl__text" data-testid="plan-limit-message">{message ? message.text : '\u00a0'}</p>
          <button type="button" className="au-pl__close" onClick={onClose} aria-label={t('planLimit.close')} data-testid="plan-limit-close">×</button>
        </div>

        {error && <div role="alert" className="au-pp-banner au-pl__banner">{error}</div>}

        <div className="au-pl__body">
          {topTier ? (
            <div className="au-pl__custom" data-testid="plan-limit-custom">
              <h3>{t('planLimit.topTierTitle')}</h3>
              <p>{t('planLimit.topTierBody', { feature: featureLabel(row, feature) })}</p>
              <a className="au-pp-cta au-pp-cta--primary au-pl__custom-cta" href={SUPPORT_HREF} data-testid="plan-limit-contact">{t('planLimit.topTierCta')}</a>
            </div>
          ) : plans ? (
            <div className={`au-pp-grid au-pl__grid au-pl__grid--${cardKeys.length}`} data-testid="plan-limit-cards">
              {cardKeys.map((key) => (
                <PlanCard
                  key={key}
                  planKey={key}
                  plan={plans.plans[key]}
                  orderedKeys={orderedKeys}
                  plans={plans.plans}
                  trialUsed={plans.trialUsed}
                  busy={busy}
                  onChoose={choose}
                  t={t}
                  language={language}
                  current={key === currentPlan}
                  allowance={allowanceLine(plans.plans[key], feature, row, t, language)}
                />
              ))}
            </div>
          ) : !error && (
            <div className="au-pl__loading" aria-busy="true" data-testid="plan-limit-loading" />
          )}
        </div>

        <div className="au-pl__foot">
          <span>{t('planLimit.referLine')}</span>
          <button type="button" className="au-pp-compare" onClick={openReferrals} data-testid="plan-limit-refer">{t('planLimit.referCta')} →</button>
          <span className="au-pl__foot-sep">·</span>
          <a href={SUPPORT_HREF}>{t('plans.contactSupport')}</a>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Small non-blocking toast: near-limit ("3 product builds left · View plans") and "Plan upgraded". */
export function QuotaToastHost() {
  const { t } = useTranslation();
  const [toast, setToast] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    const onToast = (e) => {
      const detail = e.detail || {};
      if (!detail.message) return;
      setToast(detail);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setToast(null), detail.tone === 'success' ? 6000 : 8000);
    };
    window.addEventListener(QUOTA_TOAST_EVENT, onToast);
    return () => { window.removeEventListener(QUOTA_TOAST_EVENT, onToast); if (timer.current) clearTimeout(timer.current); };
  }, []);
  if (!toast) return null;
  const viewPlans = () => {
    setToast(null);
    try { window.dispatchEvent(new CustomEvent(PLAN_LIMIT_EVENT, { detail: { feature: toast.feature || null, source: 'toast' } })); } catch { /* noop */ }
  };
  return (
    <div className={`au-quota-toast${toast.tone === 'success' ? ' au-quota-toast--success' : ''}`} role="status" data-testid="quota-toast">
      <span className="au-quota-toast__msg">{toast.message}</span>
      {toast.action === 'plans' && (
        <>
          <span className="au-quota-toast__sep" aria-hidden="true">·</span>
          <button type="button" className="au-quota-toast__link" onClick={viewPlans} data-testid="quota-toast-plans">{t('planLimit.viewPlans')}</button>
        </>
      )}
      <button type="button" className="au-quota-toast__x" onClick={() => setToast(null)} aria-label={t('planLimit.close')}>×</button>
    </div>
  );
}
